// Configuration constants for promotional popup
// Time in milliseconds before showing popup after page load
export const PROMO_POPUP_DELAY = 5000; // 5 seconds

// Time in milliseconds to wait before re-showing popup after first dismissal
export const PROMO_RESHOW_DELAY = 40000; // 40 seconds

// Maximum number of times to show the popup before permanently dismissing
export const MAX_PROMO_APPEARANCES = 20;

// Expiry date for the promotional offer
export const PROMO_EXPIRY_DATE = new Date("2026-02-15T23:59:59");

// LocalStorage key prefix
const STORAGE_PREFIX = "christmasPromo2026";

// LocalStorage keys
export const STORAGE_KEYS = {
  DISMISSED: `${STORAGE_PREFIX}Dismissed`,
  FIRST_DISMISSED_TIME: `${STORAGE_PREFIX}FirstDismissedTime`,
  DISMISS_COUNT: `${STORAGE_PREFIX}DismissCount`,
  APPEARANCE_COUNT: `${STORAGE_PREFIX}AppearanceCount`,
};


// Utility Functions

/**
 * Check if promo is still active (before expiry date)
 */
export const isPromoActive = (): boolean => {
  const now = new Date();
  return now < PROMO_EXPIRY_DATE;
};

/**
 * Get current appearance count
 */
export const getAppearanceCount = (): number => {
  const count = localStorage.getItem(STORAGE_KEYS.APPEARANCE_COUNT);
  return count ? parseInt(count, 10) : 0;
};

/**
 * Increment appearance count, auto-dismiss at max
 * Returns the new appearance count
 */
export const incrementAppearanceCount = (): number => {
  const currentCount = getAppearanceCount();
  const newCount = currentCount + 1;

  localStorage.setItem(STORAGE_KEYS.APPEARANCE_COUNT, newCount.toString());

  console.log(`Popup appearance count: ${newCount}`);

  // Auto-dismiss if reached maximum appearances
  if (newCount >= MAX_PROMO_APPEARANCES) {
    localStorage.setItem(STORAGE_KEYS.DISMISSED, "true");
    console.log("Maximum appearances reached. Popup permanently dismissed.");
  }

  return newCount;
};

/**
 * Check if permanently dismissed
 */
export const hasUserPermanentlyDismissedPromo = (): boolean => {
  return localStorage.getItem(STORAGE_KEYS.DISMISSED) === "true";
};

/**
 * Get dismiss count (for first dismissal detection)
 */
export const getDismissCount = (): number => {
  const count = localStorage.getItem(STORAGE_KEYS.DISMISS_COUNT);
  return count ? parseInt(count, 10) : 0;
};

/**
 * Mark as dismissed (increment dismiss count)
 */
export const markPromoAsDismissed = (): void => {
  const currentCount = getDismissCount();
  const newCount = currentCount + 1;
  localStorage.setItem(STORAGE_KEYS.DISMISS_COUNT, newCount.toString());

  // Store timestamp of first dismissal
  if (newCount === 1) {
    localStorage.setItem(STORAGE_KEYS.FIRST_DISMISSED_TIME, Date.now().toString());
  }
};

/**
 * Check if popup should be shown
 * Returns true if all conditions are met
 */
export const shouldShowPromo = (): boolean => {
  // Don't show if expired
  if (!isPromoActive()) {
    return false;
  }

  // Don't show if permanently dismissed
  if (hasUserPermanentlyDismissedPromo()) {
    return false;
  }

  // Don't show if reached max appearances
  if (getAppearanceCount() >= MAX_PROMO_APPEARANCES) {
    return false;
  }

  return true;
};

/**
 * Reset all state (for testing)
 */
export const resetPromoState = (): void => {
  localStorage.removeItem(STORAGE_KEYS.DISMISSED);
  localStorage.removeItem(STORAGE_KEYS.FIRST_DISMISSED_TIME);
  localStorage.removeItem(STORAGE_KEYS.DISMISS_COUNT);
  localStorage.removeItem(STORAGE_KEYS.APPEARANCE_COUNT);
  console.log("Promo state reset successfully");
};

// Expose reset function to window for easy console access
if (typeof window !== "undefined") {
  (window as any).resetPromoState = resetPromoState;
}
